/**
 * Editor Integration — editor compatibility validation.
 *
 * Decides whether a document from the Rendering Engine can actually be edited
 * in iVisora: the shapes the editor reads, the element types it renders, the
 * group model it relies on, and geometry it can lay out.
 *
 * Deterministic, non-throwing, non-mutating. It reports; it never repairs —
 * normalization does that, and only for what is safely recoverable.
 */

import {
  EDITOR_ELEMENT_TYPES, GEOMETRYLESS_TYPES, SEVERITY,
} from './sessionSchema';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const isStr = (v) => typeof v === 'string' && v.length > 0;

/**
 * @param {Object} document  a native editor document from the Rendering Engine.
 * @returns {{ valid: boolean, errors: Object[], warnings: Object[] }}
 */
export const validateEditorCompatibility = (document) => {
  const errors = [];
  const warnings = [];
  const add = (list, path, message) => list.push({ path, message });

  if (!isObj(document)) {
    add(errors, '', 'The document is not an object.');
    return { valid: false, errors, warnings };
  }
  if (!Array.isArray(document.pages) || document.pages.length === 0) {
    add(errors, 'pages', 'The editor needs at least one page.');
    return { valid: false, errors, warnings };
  }
  if (!isNum(document.canvas_width) || !isNum(document.canvas_height)) {
    add(errors, 'canvas', 'The document has no usable canvas size.');
  }

  const seenPageIds = new Set();

  document.pages.forEach((page, p) => {
    const at = `pages[${p}]`;
    if (!isObj(page)) { add(errors, at, 'A page is not an object.'); return; }
    if (!isStr(page.id)) add(errors, `${at}.id`, 'A page needs an id.');
    else if (seenPageIds.has(page.id)) add(errors, `${at}.id`, `Duplicate page id "${page.id}".`);
    else seenPageIds.add(page.id);

    if (!Array.isArray(page.elements)) { add(errors, `${at}.elements`, 'A page needs an elements array.'); return; }
    if (page.elements.length === 0) add(warnings, `${at}.elements`, 'The page is empty.');

    const ids = new Set();
    page.elements.forEach((el, e) => {
      const path = `${at}.elements[${e}]`;
      if (!isObj(el)) { add(errors, path, 'An element is not an object.'); return; }
      if (!isStr(el.id)) add(errors, `${path}.id`, 'An element needs an id.');
      else if (ids.has(el.id)) add(errors, `${path}.id`, `Duplicate element id "${el.id}".`);
      else ids.add(el.id);

      if (!EDITOR_ELEMENT_TYPES.includes(el.type)) {
        add(errors, `${path}.type`, `The editor cannot render type "${el.type}".`);
      }
      // A group's box is derived from its members; everything else must be placeable.
      if (!GEOMETRYLESS_TYPES.includes(el.type)) {
        ['x', 'y', 'width', 'height'].forEach((k) => {
          if (!isNum(el[k])) add(errors, `${path}.${k}`, `Geometry must be a finite number (${k}).`);
        });
        if (isNum(el.width) && el.width < 0) add(errors, `${path}.width`, 'Width cannot be negative.');
        if (isNum(el.height) && el.height < 0) add(errors, `${path}.height`, 'Height cannot be negative.');
      }
      if (el.type === 'text' && typeof el.text !== 'string') {
        add(errors, `${path}.text`, 'A text element needs its copy in `text`.');
      }
    });

    // The editor's grouping model: members point at a group that exists on the
    // same page, groups are not their own ancestor, and a group has members.
    const byId = new Map(page.elements.filter(isObj).map((el) => [el.id, el]));
    page.elements.filter(isObj).forEach((el, e) => {
      const path = `${at}.elements[${e}]`;
      if (el.parentId === null || el.parentId === undefined) return;
      const parent = byId.get(el.parentId);
      if (!parent) { add(errors, `${path}.parentId`, `Points at "${el.parentId}", which is not on this page.`); return; }
      if (parent.type !== 'group') add(errors, `${path}.parentId`, `Points at "${el.parentId}", which is not a group.`);

      let hop = parent;
      const walked = new Set([el.id]);
      while (hop && hop.parentId) {
        if (walked.has(hop.id)) { add(errors, `${path}.parentId`, 'Group membership is cyclic.'); break; }
        walked.add(hop.id);
        hop = byId.get(hop.parentId);
      }
    });

    page.elements.filter((el) => isObj(el) && el.type === 'group').forEach((group, g) => {
      if (!page.elements.some((el) => isObj(el) && el.parentId === group.id)) {
        add(warnings, `${at}.groups[${g}]`, `Group "${group.id}" has no members.`);
      }
    });
  });

  return { valid: errors.length === 0, errors, warnings };
};

export { SEVERITY };
